import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { appPath } from '../../utils/appRouting';
import { collection, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import {
  LayoutDashboard,
  Users,
  Sparkles,
  Settings,
  Library,
  MessageSquare,
  X,
  BookOpen,
  Calendar,
  FileText,
  Gamepad2,
  Grid3X3,
  ClipboardList,
  StickyNote,
  Clock,
  Share2,
  Presentation,
  History,
} from 'lucide-react';
import logoSvg from '../../assets/logo.svg';
import { useUserContext } from '../../contexts/UserContext';
import { useCommunityStats } from '../../community/useCommunityStats';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../lib/firebaseClient';
import { listConversationsFallback } from '../../lib/chat/chatService';
import { SOCIAL_FEATURES_ENABLED } from '../../config/featureFlags';

const SectionLabel = ({ children }) => (
  <div className="px-3 pt-5 pb-2 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
    {children}
  </div>
);

const Sidebar = ({ isMobileMenuOpen, onClose }) => {
  const navigate = useNavigate();
  const { roleType } = useUserContext();
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;
  const { stats: communityStats, hasNew: communityHasNew } = useCommunityStats({ refreshMs: 120_000, enabled: SOCIAL_FEATURES_ENABLED });
  const [unreadDmCount, setUnreadDmCount] = useState(0);
  const asideRef = useRef(null);
  const closeButtonRef = useRef(null);
  const lastFocusedElementRef = useRef(null);

  const isCurricolare = String(roleType || '').toLowerCase().includes('curricolare');

  const preferBackendDm = (() => {
    try {
      const ua = String(navigator?.userAgent || '');
      const isSafari = /safari/i.test(ua) && !/chrome|chromium|crios|fxios|edgios|android/i.test(ua);
      return isSafari;
    } catch {
      return false;
    }
  })();

  // Badge nuovi messaggi (DM): usa unreadCount salvato in conversations.
  useEffect(() => {
    if (!SOCIAL_FEATURES_ENABLED) {
      setUnreadDmCount(0);
      return;
    }
    if (!uid) {
      setUnreadDmCount(0);
      return;
    }

    let unsub = null;

    const orderedQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', uid),
      orderBy('lastMessageAt', 'desc'),
      limit(50)
    );

    const fallbackQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', uid),
      limit(50)
    );

    let pollId = null;

    const startFallback = () => {
      if (unsub) {
        unsub();
        unsub = null;
      }
      const poll = async () => {
        try {
          const convs = await listConversationsFallback(50);
          let total = 0;
          (convs || []).forEach((c) => {
            const unreadMap = c?.unreadCount || {};
            const n = Number(unreadMap?.[uid] || 0);
            if (n > 0) total += n;
          });
          setUnreadDmCount(total);
        } catch {
          setUnreadDmCount(0);
        }
      };
      void poll();
      if (pollId) window.clearInterval(pollId);
      pollId = window.setInterval(() => void poll(), 30_000);
    };

    // Safari (specie Navigazione Privata): preferisci backend polling.
    if (preferBackendDm) {
      startFallback();
      return () => {
        if (unsub) unsub();
        unsub = null;
        if (pollId) window.clearInterval(pollId);
      };
    }

    const start = (q) => {
      if (unsub) unsub();
      unsub = onSnapshot(
        q,
        (snap) => {
          let total = 0;
          snap.docs.forEach((d) => {
            const data = d.data() || {};
            const unreadMap = data.unreadCount || {};
            const n = Number(unreadMap?.[uid] || 0);
            if (n > 0) total += n;
          });
          setUnreadDmCount(total);
        },
        (err) => {
          const msg = String(err?.message || '');
          const code = String(err?.code || '').toLowerCase();
          const isPerm = code.includes('permission') || msg.toLowerCase().includes('missing or insufficient permissions');
          if (isPerm) {
            startFallback();
            return;
          }
          // Se manca indice (array-contains + orderBy), ripiega su query senza orderBy.
          if (msg.toLowerCase().includes('requires an index') || msg.toLowerCase().includes('failed_precondition')) {
            start(fallbackQuery);
            return;
          }
          // In caso di permessi, evitiamo spam UI: badge = 0
          setUnreadDmCount(0);
        }
      );
    };

    start(orderedQuery);

    return () => {
      if (unsub) unsub();
      unsub = null;
      if (pollId) window.clearInterval(pollId);
    };
  }, [uid, preferBackendDm]);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      return;
    }

    lastFocusedElementRef.current = document.activeElement;

    // Focus iniziale: close button
    const focusTimer = window.setTimeout(() => {
      closeButtonRef.current?.focus?.();
    }, 0);

    const onKeyDown = (event) => {
      if (!isMobileMenuOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const root = asideRef.current;
      if (!root) return;

      const focusable = Array.from(
        root.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => {
        const element = el;
        return !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true';
      });

      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || active === root) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);

      // Ripristina focus al trigger (best-effort)
      const lastFocused = lastFocusedElementRef.current;
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    };
  }, [isMobileMenuOpen, onClose]);

  const navGroups = [
    {
      key: 'panoramica',
      label: 'Panoramica',
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
        ...(SOCIAL_FEATURES_ENABLED
          ? [
              { icon: Share2, label: 'Community didattica', path: '/community' },
              { icon: MessageSquare, label: 'Messaggi', path: '/community/messages' },
            ]
          : []),
      ]
    },
    {
      key: 'didattica',
      label: 'Didattica',
      items: [
        { icon: Sparkles, label: 'AI Didattica', path: '/ai-didattica' },
        { icon: Grid3X3, label: 'CAA (Pittogrammi)', path: '/caa' },
        { icon: Gamepad2, label: 'Quiz Live', path: '/quiz-live' },
        { icon: StickyNote, label: 'Bacheca collaborativa', path: '/bacheca' },
        { icon: Presentation, label: 'Le mie presentazioni', path: '/presentazioni' }
      ]
    },
    {
      key: 'alunni',
      label: isCurricolare ? 'Classi e Documenti' : 'Alunni e Documenti',
      items: [
        { icon: Users, label: isCurricolare ? 'Le mie classi' : 'I miei alunni', path: '/students' },
        { icon: BookOpen, label: 'Diario di bordo', path: '/diario' },
        { icon: ClipboardList, label: 'Appello', path: '/appello' }
      ]
    },
    {
      key: 'organizzazione',
      label: 'Organizzazione',
      items: [
        { icon: Clock, label: 'Orario', path: '/orario' },
        { icon: Calendar, label: 'Calendario', path: '/calendario' },
        { icon: Library, label: 'Libreria materiali', path: '/libreria-materiali' },
        { icon: History, label: 'Storico anni scolastici', path: '/storico-anni-scolastici' }
      ]
    },
    {
      key: 'sistema',
      label: 'Sistema',
      items: [
        { icon: Settings, label: 'Impostazioni', path: '/settings' },
      ]
    }
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        ref={asideRef}
        className={`
        fixed lg:static
        inset-y-0 left-0
        w-64
        bg-background/80 backdrop-blur-xl border-r border-border/50
        text-foreground
        transform transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        z-50
        flex flex-col
        h-[100dvh]
        min-h-0
      `}
        role={isMobileMenuOpen ? 'dialog' : undefined}
        aria-modal={isMobileMenuOpen ? true : undefined}
        aria-label={isMobileMenuOpen ? 'Menu di navigazione' : undefined}
      >
        <div className="p-6 flex-1 flex flex-col min-h-0">
          {/* Header */}
          <div className="flex items-center justify-between gap-2 mb-4 lg:mb-8">
            {/* Desktop sidebar brand */}
            <div className="hidden lg:flex items-center gap-2">
              <img
                src={logoSvg}
                alt="Sostegno Pro+"
                className="w-10 h-10"
                draggable={false}
              />
              <div>
                <h2 className="font-bold text-[15px] leading-tight text-foreground">Sostegno Pro+</h2>
                <p className="text-xs text-muted-foreground">Workspace</p>
              </div>
            </div>

            {/* Mobile drawer header: niente duplicazione del nome (solo mini-logo opzionale) */}
            <div className="lg:hidden flex items-center gap-2">
              <img
                src={logoSvg}
                alt="Sostegno Pro+"
                className="w-8 h-8"
                draggable={false}
              />
            </div>
            {/* Close button mobile */}
            <button
              onClick={onClose}
              className="lg:hidden p-1 hover:bg-accent rounded-lg transition-colors"
              aria-label="Chiudi menu"
              ref={closeButtonRef}
            >
              <X className="w-6 h-6 text-foreground" />
            </button>
          </div>

          <nav className="flex-1 min-h-0 overflow-y-auto -mx-2 pr-1 overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch]">
            <div className="space-y-1">
              {navGroups.map((g) => (
                <div key={g.key}>
                  <SectionLabel>{g.label}</SectionLabel>
                  <div className="space-y-1 px-2">
                    {g.items.map((item) => (
                      <NavLink
                        key={item.path}
                        to={appPath(item.path)}
                        onClick={onClose}
                        className={({ isActive }) =>
                          `group relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ` +
                          (isActive
                            ? 'bg-violet-500/10 text-violet-600 dark:text-violet-300 shadow-[0_0_20px_-5px_rgba(139,92,246,0.2)]'
                            : 'text-muted-foreground hover:bg-accent hover:text-foreground')
                        }
                      >
                        {({ isActive }) => (
                          <>
                            {isActive && (
                              <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-violet-600 dark:bg-violet-400" />
                            )}
                            <item.icon className={`w-[18px] h-[18px] ${isActive
                              ? 'text-violet-600 dark:text-violet-300'
                              : 'text-muted-foreground group-hover:text-foreground'
                              }`}
                            />
                            <span className="text-sm font-medium">{item.label}</span>

                            {item.path === '/community' ? (
                              <span className="ml-auto flex items-center gap-2">
                                {communityHasNew ? (
                                  <span className="relative inline-flex items-center">
                                    <span className="absolute -inset-1 rounded-full bg-violet-500/30 blur" />
                                    <span className="relative inline-flex items-center rounded-full bg-violet-500 text-white px-2 py-0.5 text-[10px] font-extrabold">
                                      Nuovo
                                    </span>
                                  </span>
                                ) : null}

                                {Number(communityStats?.recent_count_24h || 0) > 0 ? (
                                  <span className="text-[11px] text-muted-foreground">
                                    +{Number(communityStats?.recent_count_24h || 0)}
                                  </span>
                                ) : null}
                              </span>
                            ) : null}

                            {item.path === '/community/messages' ? (
                              <span className="ml-auto flex items-center gap-2">
                                {unreadDmCount > 0 ? (
                                  <span className="relative inline-flex items-center">
                                    <span className="absolute -inset-1 rounded-full bg-sky-500/30 blur" />
                                    <span className="relative inline-flex items-center rounded-full bg-sky-500 text-white px-2 py-0.5 text-[10px] font-extrabold">
                                      {unreadDmCount > 99 ? '99+' : unreadDmCount}
                                    </span>
                                  </span>
                                ) : null}
                              </span>
                            ) : null}
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </nav>

        </div>
      </aside>
    </>
  );
};

export default Sidebar;
